import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import CircularProgress from "@busbud/design-system-components/dist/CircularProgress";
import { Alert, Label } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { AccountReduxState } from "@app/modules/account";
import type { CreditCard } from "@app/types/napi-types";

import { PaymentMethod } from "./payment-method";
import { openModal } from "../../../../../store/state/modals";
import { removedCreditCard } from "../../../../../tracking/account-tracking";
import {
  deleteCreditCard as deleteCreditCardAction,
  initCreditCards
} from "../../../state/credit-cards";

interface Props {
  onError: (error_message: string | null) => void;
  onSuccess: () => void;
}

export const PaymentMethodsSection: React.FC<Props> = ({
  onError,
  onSuccess
}) => {
  const { api, tracker, translator } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const dispatch = useDispatch();
  const credit_cards = useSelector<AccountReduxState, CreditCard[]>(
    state => state.credit_cards
  );
  const user_id = useSelector<AccountReduxState, number>(
    state => state.user.id
  );

  useEffect(() => {
    api
      .getCreditCards(user_id)
      .then(credit_cards => {
        dispatch(initCreditCards(credit_cards));
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [api, dispatch, user_id]);

  const resetError = () => onError(null);

  const askDeleteConfirmation = (credit_card: CreditCard) => {
    resetError();

    dispatch(
      openModal({
        type: "delete_credit_card",
        onConfirm: () => deleteCreditCard(credit_card)
      })
    );
  };

  const deleteCreditCard = async (credit_card: CreditCard) => {
    try {
      await api.deleteCreditCard(user_id, credit_card);
      tracker.track(removedCreditCard());
      dispatch(deleteCreditCardAction(credit_card.id));
      onSuccess();
    } catch (err) {
      onError(translator.t("!accounts.settings.cc-retrieval-error"));
    }
  };

  return (
    <div className="flex flex-col gap-200">
      <Label fontWeight="bold" size="lg" as="span">
        {translator.t("!checkout.payment.dropin-information")}
      </Label>
      {!!loading && <CircularProgress />}
      {!!error && (
        <Alert severity="negative">
          {translator.t("!accounts.settings.cc-retrieving-err")}
        </Alert>
      )}
      {!credit_cards.length && !loading && !error ? (
        <Label className="text-color-tertiary" as="span" size="md">
          {translator.t("!accounts.settings.empty-cc")}
        </Label>
      ) : (
        credit_cards.map(credit_card => (
          <PaymentMethod
            credit_card={credit_card}
            key={credit_card.id}
            user_id={user_id}
            onDelete={() => askDeleteConfirmation(credit_card)}
            onError={onError}
            onSuccess={onSuccess}
          />
        ))
      )}
    </div>
  );
};
